import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Loader2, Unplug, Send } from "lucide-react";
import { useAdminAccess } from "@/lib/adminAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-sending`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const when = (s) => (s ? new Date(s).toLocaleString() : "—");

const StatusBanner = ({ s }) => {
  if (s.connected) return (
    <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="gmail-status-connected">
      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
      <div><p className="font-semibold text-emerald-800">Sending through {s.email}</p>
        <p className="text-sm text-emerald-700">Connected {when(s.connected_at)}{s.connected_by ? ` by ${s.connected_by}` : ""} · {s.sent_count} sent · last sent {when(s.last_sent_at)}</p>
        {s.email !== s.expected_email && <p className="text-sm text-amber-700 mt-1">Heads up: this isn't {s.expected_email}.</p>}
      </div>
    </div>
  );
  return (
    <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4" data-testid="gmail-status-disconnected">
      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
      <div><p className="font-semibold text-amber-800">{s.needs_reconnect ? `Gmail needs to be reconnected (${s.email})` : "Gmail is not connected"}</p>
        <p className="text-sm text-amber-700">Emails are going out through the backup service (Resend) until Gmail is connected.</p></div>
    </div>
  );
};

export default function AdminEmailSending() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [form, setForm] = useState(null);
  const canManage = useAdminAccess().can("settings_gmail", "manage");
  const load = useCallback(() => axios.get(`${API}/status`, auth()).then((r) => setS(r.data)).catch(() => toast.error("Couldn't load email settings")), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } setBusy(false); };
  const connect = () => run(async () => { await axios.post(`${API}/connect`, form, auth()); toast.success(`Connected ${form.email}`); setForm(null); load(); });
  const disconnect = () => window.confirm("Disconnect Gmail? Emails will go out through Resend.") && run(async () => { await axios.post(`${API}/disconnect`, {}, auth()); toast.success("Disconnected"); load(); });
  const test = () => run(async () => { await axios.post(`${API}/test`, { to: testTo }, auth()); toast.success(`Test email sent to ${testTo}`); load(); });

  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return (
    <Card className="border-slate-200" data-testid="admin-email-sending">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-teal" /><h2 className="font-heading font-semibold text-navy text-lg">Email Sending</h2></div>
        <p className="text-sm text-slate-500">All website emails (leads, applications, sales digests, alerts) are sent from this Gmail / Google Workspace account. If it fails, emails go through Resend and the Super Admins get an alert.</p>
        <StatusBanner s={s} />
        {s.last_error && <p className="text-sm text-red-600" data-testid="gmail-last-error">Last problem ({when(s.last_error_at)}): {s.last_error}</p>}
        {canManage && !form && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setForm({ email: s.email || s.expected_email, app_password: "" })} className="bg-teal hover:bg-teal-dark text-white" data-testid="gmail-connect-btn">
              <Mail className="w-4 h-4 mr-2" />{s.connected ? "Change account or app password" : "Connect Gmail"}
            </Button>
            {(s.connected || s.needs_reconnect) && <Button variant="outline" onClick={disconnect} disabled={busy} className="text-red-600" data-testid="gmail-disconnect-btn"><Unplug className="w-4 h-4 mr-2" />Disconnect</Button>}
          </div>
        )}
        {form && (
          <div className="space-y-3 max-w-md rounded-lg border border-slate-200 p-4" data-testid="gmail-connect-form">
            <div><Label>Gmail / Workspace address</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="gmail-email-input" /></div>
            <div><Label>App password</Label><Input type="password" autoComplete="new-password" placeholder="16 characters" value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} data-testid="gmail-app-password-input" />
              <p className="text-xs text-slate-500 mt-1">Create one at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-teal underline">myaccount.google.com/apppasswords</a> while signed in as this account (2-Step Verification must be on). It's stored encrypted.</p></div>
            <div className="flex gap-2">
              <Button onClick={connect} disabled={busy || !form.email || form.app_password.replace(/\s/g, "").length < 8} className="bg-teal hover:bg-teal-dark text-white" data-testid="gmail-save-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Check &amp; connect</Button>
              <Button variant="ghost" onClick={() => setForm(null)} data-testid="gmail-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}
        {canManage && s.connected && (
          <div className="flex gap-2 max-w-md">
            <Input type="email" placeholder="Send a test email to…" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="gmail-test-to" />
            <Button variant="outline" onClick={test} disabled={busy || !testTo} data-testid="gmail-test-btn"><Send className="w-4 h-4 mr-2" />Test</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
